/**
 * Client-side mirror of ananunapi/utils/flattenPermissionKeys.js (flatten only; used when saving roles).
 */

const AI_CRUD_KEYS = {"useAssistant": "ai.assistant.use", "voiceCommands": "ai.assistant.voice", "viewHistory": "ai.assistant.view_history", "approveActions": "ai.assistant.approve_actions", "viewLogs": "ai.admin.view_logs"};

const LEGACY_PERMISSION_ALIASES = {
	itTicketSystem: ["ticketsDomain_it"],
	hrTicketSystem: ["ticketsDomain_hr"]
};

/**
 * Forms module uses granular flat keys while the role UI grants standard CRUD.
 * Keep in sync with ananunapi/utils/flattenPermissionKeys.js.
 */
const FORMS_KEYS_FROM_CRUD = {
	read: [
		"forms.view",
		"forms.answer",
		"forms.viewResponses.own",
		"forms.analytics",
		"forms.export"
	],
	update: [
		"forms.edit.own",
		"forms.edit.all",
		"forms.viewResponses.all",
		"forms.publish",
		"forms.manageAccess",
		"forms.viewAnonymousRawData"
	],
	delete: ["forms.delete.own", "forms.delete.all"]
};

export const FORMS_KEY_IMPLIED_BY_CRUD = (() => {
	const map = Object.create(null);
	Object.entries(FORMS_KEYS_FROM_CRUD).forEach(([crudAction, keys]) => {
		const crudKey = `forms.${crudAction}`;
		keys.forEach((k) => {
			map[k] = crudKey;
		});
	});
	return map;
})();

function expandedPermissionNames(mod) {
	if (!mod) return [];
	return [mod, ...(LEGACY_PERMISSION_ALIASES[mod] || [])];
}

function appendFormsKeysFromCrud(keys, crud) {
	if (!crud) return;
	if (crud.read) FORMS_KEYS_FROM_CRUD.read.forEach((k) => keys.push(k));
	if (crud.update) FORMS_KEYS_FROM_CRUD.update.forEach((k) => keys.push(k));
	if (crud.delete) FORMS_KEYS_FROM_CRUD.delete.forEach((k) => keys.push(k));
}

export function expandFormsCrudInKeySet(keySet) {
	if (!keySet || typeof keySet.has !== "function") return keySet;
	if (keySet.has("forms.read")) {
		FORMS_KEYS_FROM_CRUD.read.forEach((k) => keySet.add(k));
	}
	if (keySet.has("forms.update")) {
		FORMS_KEYS_FROM_CRUD.update.forEach((k) => keySet.add(k));
	}
	if (keySet.has("forms.delete")) {
		FORMS_KEYS_FROM_CRUD.delete.forEach((k) => keySet.add(k));
	}
	return keySet;
}

export function flattenOnePermission(perm) {
	const keys = [];
	const mod = perm && perm.name;
	if (!mod) return keys;
	const moduleNames = expandedPermissionNames(mod);

	const crud = perm.crud || {};
	const baseCrud = new Set(["create", "read", "update", "delete"]);
	moduleNames.forEach((moduleName) => {
		if (crud.create) keys.push(`${moduleName}.create`);
		if (crud.read) keys.push(`${moduleName}.read`);
		if (crud.update) keys.push(`${moduleName}.update`);
		if (crud.delete) keys.push(`${moduleName}.delete`);
		Object.keys(crud).forEach((k) => {
			if (baseCrud.has(k) || !crud[k]) return;
			keys.push(moduleName === "ai" && AI_CRUD_KEYS[k] ? AI_CRUD_KEYS[k] : `${moduleName}.${k}`);
		});
		if (moduleName === "forms") appendFormsKeysFromCrud(keys, crud);
	});

	(perm.inputs || []).forEach((inp) => {
		const field = inp && inp.name;
		if (!field) return;
		moduleNames.forEach((moduleName) => {
			if (inp.create !== undefined && inp.create) keys.push(`${moduleName}.${field}.create`);
			if (inp.read) keys.push(`${moduleName}.${field}.read`);
			if (inp.update) keys.push(`${moduleName}.${field}.update`);
			if (inp.delete !== undefined && inp.delete) keys.push(`${moduleName}.${field}.delete`);
		});
	});

	return keys;
}

export function flattenPermissionsArray(permissions) {
	const set = new Set();
	(permissions || []).forEach((p) => {
		flattenOnePermission(p).forEach((k) => set.add(k));
	});
	return Array.from(set).sort();
}

export function collectKeysFromRole(role) {
	const keys = new Set();
	if (!role) return keys;
	if (Array.isArray(role.flatPermissions) && role.flatPermissions.length) {
		role.flatPermissions.forEach((k) => keys.add(k));
	}
	(role.permissions || []).forEach((p) => {
		flattenOnePermission(p).forEach((k) => keys.add(k));
	});
	expandFormsCrudInKeySet(keys);
	return keys;
}

export function collectKeysFromUserRoles(user) {
	const keys = new Set();
	if (!user) return keys;
	const roles = Array.isArray(user.role) ? user.role : user.role ? [user.role] : [];
	roles.forEach((r) => {
		collectKeysFromRole(r).forEach((k) => keys.add(k));
	});
	return keys;
}

/** All flat keys implied by a nested permissions[] array, regardless of true/false. Mirrors backend. */
export function flatKeysFromPermissionDoc(doc) {
	if (!doc || !doc.name) return [];
	const mod = doc.name;
	const keys = [];
	const baseCrud = new Set(["create", "read", "update", "delete"]);
	keys.push(`${mod}.create`, `${mod}.read`, `${mod}.update`, `${mod}.delete`);
	Object.keys(doc.crud || {}).forEach((k) => {
		if (baseCrud.has(k)) return;
		keys.push(`${mod}.${k}`);
	});
	(doc.inputs || []).forEach((inp) => {
		const f = inp && inp.name;
		if (!f) return;
		if (inp.create !== undefined) keys.push(`${mod}.${f}.create`);
		keys.push(`${mod}.${f}.read`, `${mod}.${f}.update`);
		if (inp.delete !== undefined) keys.push(`${mod}.${f}.delete`);
	});
	return [...new Set(keys)].sort();
}

/** Field keys a role has explicitly toggled (checked or unchecked) — distinct from granted keys. */
export function collectExplicitFieldKeysFromRole(role) {
	const keys = new Set();
	(role?.permissions || []).forEach((p) => {
		flatKeysFromPermissionDoc(p).forEach((k) => keys.add(k));
	});
	return keys;
}

export function collectExplicitFieldKeysFromUserRoles(user) {
	const keys = new Set();
	if (!user) return keys;
	const roles = Array.isArray(user.role) ? user.role : user.role ? [user.role] : [];
	roles.forEach((r) => {
		collectExplicitFieldKeysFromRole(r).forEach((k) => keys.add(k));
	});
	return keys;
}
